import * as React from 'react'
import type { GeckoComponentDataFrame, GeckoMeasurementUnit } from 'types'

/**
 * A user override for a per-CDF display value (color threshold, filtered value
 * range). Controlled when `onControlledChange` is provided — the parent owns
 * the override — otherwise held internally, scoped to the CDF it was set on.
 * The effective value falls back to `fallback` at render time; nothing is
 * synced into state, so a parent resetting a controlled override to undefined
 * simply re-derives the fallback — there is no effect-ordering handshake to
 * race with.
 */
export function useCdfDisplayOverride<
  T extends { convertTo: (unit: GeckoMeasurementUnit | undefined) => T },
>({
  selectedCDF,
  controlledValue,
  onControlledChange,
  fallback,
}: {
  selectedCDF: GeckoComponentDataFrame | undefined
  controlledValue?: T
  onControlledChange?: (value: T | undefined) => void
  fallback?: T
}) {
  const [internal, setInternal] = React.useState<{
    cdfId: string | undefined
    value: T | undefined
  }>()
  const isControlled = onControlledChange !== undefined
  const override = isControlled
    ? controlledValue
    : internal?.cdfId === selectedCDF?.id
      ? internal?.value
      : undefined
  const value = React.useMemo(
    () =>
      (override ?? fallback)?.convertTo(
        selectedCDF?.data.params.measurement_unit
      ),
    [fallback, override, selectedCDF?.data.params.measurement_unit]
  )
  const setValue = React.useCallback(
    (next: T | undefined) => {
      if (isControlled) {
        onControlledChange?.(next)
        return
      }

      setInternal({ cdfId: selectedCDF?.id, value: next })
    },
    [isControlled, onControlledChange, selectedCDF?.id]
  )
  return [value, setValue] as const
}
